import {getTestProps} from '@benchling/js-test-utils';
import {Badge, createStyles, Flex, Group, Text} from '@mantine/core';
import React, {useCallback, useContext} from 'react';

import {formatCanvasVersionLabel} from 'canvas-core/canvasVersionUtil';
import {
  ChatCanvasVersionSummary,
  useChatCanvasVersionState,
} from 'canvas-core/chat/useChatCanvasVersionState';
import {ChatContext} from 'llm/chat-library/ChatContext';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {CenteredSpinner} from 'ui-library/Spinner';

export interface ChatCanvasCardProps {
  chatSessionId: string | null;
  canvasDefinitionId: string;
  canvasVersionId: string;
}

const useStyles = createStyles((theme) => ({
  card: {
    width: '100%',
    marginTop: theme.spacing.md,
    marginBottom: theme.spacing.md,
    border: `1px solid ${theme.colors.gray[2]}`,
    borderRadius: theme.radius.sm,
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing.md,
  },
  iconTile: {
    alignItems: 'center',
    backgroundColor: theme.colors.blue[0],
    borderRadius: theme.radius.sm,
    color: theme.colors.blue[8],
    display: 'flex',
    height: 32,
    justifyContent: 'center',
    width: 32,
    flexShrink: 0,
  },
  title: {
    fontSize: theme.fontSizes.sm,
    fontWeight: 600,
    lineHeight: 1.3,
  },
  actions: {
    alignItems: 'center',
    flexShrink: 0,
    gap: theme.spacing.xs,
  },
  loadingCard: {
    minHeight: 48,
  },
}));

function ChatCanvasCardContent({
  canvasDefinitionId,
  canvasVersionId,
  canvasVersion,
}: {
  canvasDefinitionId: string;
  canvasVersionId: string;
  canvasVersion: ChatCanvasVersionSummary;
}): JSX.Element {
  const {classes} = useStyles();
  const chatContext = useContext(ChatContext);

  const handleExpand = useCallback(() => {
    chatContext?.openCanvas({canvasDefinitionId, canvasVersionId});
  }, [chatContext, canvasDefinitionId, canvasVersionId]);

  return (
    <Flex className={classes.card} {...getTestProps({component: 'ChatCanvasCard', element: 'root'})}>
      <Group align="center" noWrap={true} spacing="sm">
        <div className={classes.iconTile}>
          <BenchlingIcon icon="canvas_definition" />
        </div>
        <Group align="center" noWrap={true} spacing="xs">
          <Text className={classes.title}>{canvasVersion.name}</Text>
          <Badge color="gray" size="xs" variant="light">
            {formatCanvasVersionLabel(canvasVersion.versionIndex)}
          </Badge>
        </Group>
      </Group>
      <Group className={classes.actions} noWrap={true} spacing="xs">
        <Button
          buttonStyle={ButtonStyle.SECONDARY_WHITE}
          size={ButtonSize.SMALL}
          icon="expand-right-full"
          onClick={handleExpand}
          aria-label="Expand canvas preview"
          {...getTestProps({component: 'ChatCanvasCard', element: 'expandButton'})}
        />
      </Group>
    </Flex>
  );
}

export default function ChatCanvasCard({
  chatSessionId,
  canvasDefinitionId,
  canvasVersionId,
}: ChatCanvasCardProps): JSX.Element {
  const {classes} = useStyles();
  const queryResult = useChatCanvasVersionState(chatSessionId, canvasDefinitionId, canvasVersionId);

  if (queryResult.type === 'skipped') {
    return (
      <InlineBanner
        bannerType={InlineBannerType.ERROR}
        text="Canvas is not available for your organization."
      />
    );
  }

  if (queryResult.type === 'pending') {
    return (
      <Flex align="center" className={`${classes.card} ${classes.loadingCard}`} justify="center">
        <CenteredSpinner />
      </Flex>
    );
  }

  if (queryResult.type === 'error') {
    return <InlineBanner bannerType={InlineBannerType.ERROR} text="Unable to load canvas details." />;
  }

  return (
    <ChatCanvasCardContent
      canvasDefinitionId={canvasDefinitionId}
      canvasVersionId={canvasVersionId}
      canvasVersion={queryResult.canvasVersion}
    />
  );
}
